/**
 * 📊 DATATABLE PERFORMANCE PROFILER
 * ==================================
 * 
 * Herramienta para medir y analizar el rendimiento del componente Datatable (vigilantes).
 * Mide operaciones críticas como:
 * - Carga de vigilantes filtrados
 * - Carga de todos los vigilantes
 * - Población de información de tiendas/clientes
 * - Filtrado de datos
 * - Eliminación/restauración
 * 
 * @author Sistema de Profiling
 * @version 1.0.0
 */

class DatatablePerformanceProfiler {
    constructor() {
        this.sessions = new Map();
        this.currentSession = null;
        this.isEnabled = true; // Cambiar a false para desactivar profiling
    }

    /**
     * Inicia una nueva sesión de profiling
     * @param {string} component - Nombre del componente o operación
     * @param {Object} metadata - Metadata adicional
     */
    startSession(component, metadata = {}) {
        if (!this.isEnabled) return;

        const sessionId = `${component}_${Date.now()}`;
        
        this.currentSession = {
            sessionId,
            component,
            startTime: performance.now(),
            measurements: new Map(),
            metadata: {
                ...metadata,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
            }
        };

        this.sessions.set(sessionId, this.currentSession);
        
        console.log(`📊 [DATATABLE PROFILING] Iniciando sesión: ${sessionId}`);
        console.log(`📋 Operación: ${component}`);
        if (Object.keys(metadata).length > 0) {
            console.log(`📝 Metadata:`, metadata);
        }

        return sessionId;
    }

    /**
     * Inicia una medición dentro de la sesión actual
     * @param {string} measurementName - Nombre de la medición
     * @param {Object} details - Detalles adicionales
     */
    startMeasurement(measurementName, details = {}) {
        if (!this.isEnabled || !this.currentSession) {
            if (!this.currentSession && this.isEnabled) {
                console.warn(`⚠️ [DATATABLE PROFILING] No hay sesión activa para medir: ${measurementName}`);
            }
            return;
        }

        const measurement = {
            name: measurementName,
            startTime: performance.now(),
            details,
            subMeasurements: []
        };

        this.currentSession.measurements.set(measurementName, measurement);
        
        console.log(`⏱️ [DATATABLE PROFILING] ⏯️ Iniciando: ${measurementName}`, details);

        return measurementName;
    }

    /**
     * Finaliza una medición
     * @param {string} measurementName - Nombre de la medición a finalizar
     * @param {Object} result - Resultado de la medición
     */
    endMeasurement(measurementName, result = {}) {
        if (!this.isEnabled || !this.currentSession) {
            if (!this.currentSession && this.isEnabled) {
                console.warn(`⚠️ [DATATABLE PROFILING] No hay sesión activa para finalizar: ${measurementName}`);
            }
            return;
        }

        const measurement = this.currentSession.measurements.get(measurementName);
        
        if (!measurement) {
            console.warn(`⚠️ [DATATABLE PROFILING] Medición no encontrada: ${measurementName}`);
            return;
        }

        measurement.endTime = performance.now();
        measurement.duration = measurement.endTime - measurement.startTime;
        measurement.result = result;

        console.log(`⏱️ [DATATABLE PROFILING] ⏹️ Finalizado: ${measurementName}`);
        console.log(`⏰ Duración: ${measurement.duration.toFixed(2)}ms`);
        
        if (Object.keys(result).length > 0) {
            console.log(`📊 Resultado:`, result);
        }

        return measurement.duration;
    }

    /**
     * Finaliza la sesión de profiling y genera el reporte
     * @param {Object} summary - Resumen adicional
     */
    endSession(summary = {}) {
        if (!this.isEnabled || !this.currentSession) {
            if (!this.currentSession && this.isEnabled) {
                console.warn(`⚠️ [DATATABLE PROFILING] No hay sesión activa para finalizar`);
            }
            return;
        }

        this.currentSession.endTime = performance.now();
        this.currentSession.totalDuration = this.currentSession.endTime - this.currentSession.startTime;
        this.currentSession.summary = summary;

        // Generar reporte
        this.generateReport(this.currentSession);

        const sessionId = this.currentSession.sessionId;
        this.currentSession = null;

        return sessionId;
    }

    /**
     * Genera un reporte detallado de la sesión
     * @param {Object} session - Sesión a reportar
     */
    generateReport(session) {
        console.log(`📊 ================================================`);
        console.log(`📊 [DATATABLE PROFILING] RESUMEN DE SESIÓN: ${session.sessionId}`);
        console.log(`📊 ================================================`);
        console.log(`🖥️ Operación: ${session.component}`);
        console.log(`⏰ Tiempo total: ${session.totalDuration.toFixed(2)}ms`);
        console.log(`📅 Timestamp: ${session.metadata.timestamp}`);
        
        if (session.metadata.serviceId) {
            console.log(`🔑 Servicio: ${session.metadata.serviceId}`);
        }
        if (session.metadata.vigilantesCount !== undefined) {
            console.log(`👥 Vigilantes: ${session.metadata.vigilantesCount}`);
        }
        
        console.log(`📋 Desglose por función:`);
        console.log(`------------------------------------------------`);

        // Ordenar mediciones por tiempo de inicio
        const sortedMeasurements = Array.from(session.measurements.values())
            .filter(m => m.endTime) // Solo las completadas
            .sort((a, b) => a.startTime - b.startTime);

        sortedMeasurements.forEach((measurement, index) => {
            const percentage = ((measurement.duration / session.totalDuration) * 100).toFixed(1);
            const icon = measurement.duration > 2000 ? '🔴' : measurement.duration > 1000 ? '🟡' : '✅';
            
            console.log(`${index + 1}. ${icon} ${measurement.name} ${measurement.duration.toFixed(2)}ms (${percentage}%)`);
            
            // Mostrar detalles si existen
            if (Object.keys(measurement.details).length > 0) {
                console.log(`   📝 Detalles:`, measurement.details);
            }
            
            // Mostrar resultados si existen
            if (measurement.result && Object.keys(measurement.result).length > 0) {
                console.log(`   📊 Resultado:`, measurement.result);
            }
        });

        console.log(`------------------------------------------------`);
        console.log(`📊 Total de mediciones: ${sortedMeasurements.length}`);
        
        // Resumen adicional
        if (session.summary && Object.keys(session.summary).length > 0) {
            console.log(`📋 Resumen adicional:`, session.summary);
        }
        
        // Análisis de rendimiento
        this.analyzePerformance(session, sortedMeasurements);
        
        console.log(`📊 ================================================`);
    }

    /**
     * Analiza el rendimiento y genera recomendaciones
     * @param {Object} session - Sesión a analizar
     * @param {Array} measurements - Mediciones ordenadas
     */
    analyzePerformance(session, measurements) {
        console.log(`\n🔍 ANÁLISIS DE RENDIMIENTO:`);
        console.log(`------------------------------------------------`);

        const totalDuration = session.totalDuration;
        
        // Clasificar rendimiento
        let performanceLevel = '';
        let recommendations = [];

        if (totalDuration < 1000) {
            performanceLevel = '🚀 EXCELENTE';
            console.log(`${performanceLevel} - Tiempo de carga < 1s`);
        } else if (totalDuration < 3000) {
            performanceLevel = '✅ BUENO';
            console.log(`${performanceLevel} - Tiempo de carga < 3s`);
        } else if (totalDuration < 10000) {
            performanceLevel = '⚠️ ACEPTABLE';
            console.log(`${performanceLevel} - Tiempo de carga < 10s`);
            recommendations.push('Considerar optimizaciones para reducir tiempo de carga');
        } else {
            performanceLevel = '🔴 MEJORABLE';
            console.log(`${performanceLevel} - Tiempo de carga > 10s`);
            recommendations.push('Requiere optimización urgente');
        }

        // Identificar cuellos de botella
        const bottlenecks = measurements.filter(m => m.duration > 2000);
        
        if (bottlenecks.length > 0) {
            console.log(`\n🔴 Cuellos de botella identificados (> 2s):`);
            bottlenecks.forEach(bottleneck => {
                console.log(`   - ${bottleneck.name}: ${bottleneck.duration.toFixed(2)}ms`);
                
                // Recomendaciones específicas
                if (bottleneck.name.includes('Fetch') || bottleneck.name.includes('Load')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Considerar caché o carga en lote`);
                }
                if (bottleneck.name.includes('Populate') || bottleneck.name.includes('Tiendas')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Considerar pre-carga o lazy loading`);
                }
                if (bottleneck.name.includes('Filter')) {
                    recommendations.push(`Optimizar ${bottleneck.name}: Aplicar filtros en servidor (Firebase)`);
                }
            });
        }

        // Recomendaciones generales
        if (recommendations.length > 0) {
            console.log(`\n💡 Recomendaciones:`);
            recommendations.forEach((rec, index) => {
                console.log(`   ${index + 1}. ${rec}`);
            });
        }

        // Información sobre datos procesados
        if (session.summary.vigilantesProcessed) {
            const vigilantesPerSecond = (session.summary.vigilantesProcessed / (totalDuration / 1000)).toFixed(1);
            console.log(`\n📊 Rendimiento de procesamiento:`);
            console.log(`   - Vigilantes procesados: ${session.summary.vigilantesProcessed}`);
            console.log(`   - Velocidad: ${vigilantesPerSecond} vigilantes/segundo`);
        }

        // Advertencias específicas
        if (totalDuration > 5000 && session.summary.vigilantesProcessed < 100) {
            console.log(`\n⚠️ ADVERTENCIA:`);
            console.log(`   Tiempo muy alto para pocos vigilantes.`);
            console.log(`   Posibles causas:`);
            console.log(`   - Consultas individuales por tienda (considerar batch)`);
            console.log(`   - Sin caché de tiendas`);
            console.log(`   - Población secuencial (considerar paralela)`);
        }
    }

    /**
     * Obtiene un reporte de todas las sesiones
     */
    getAllSessions() {
        return Array.from(this.sessions.values());
    }

    /**
     * Limpia todas las sesiones almacenadas
     */
    clearSessions() {
        this.sessions.clear();
        this.currentSession = null;
        console.log(`🗑️ [DATATABLE PROFILING] Sesiones limpiadas`);
    }

    /**
     * Habilita o deshabilita el profiling
     * @param {boolean} enabled - Estado del profiling
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
        console.log(`📊 [DATATABLE PROFILING] Profiling ${enabled ? 'habilitado' : 'deshabilitado'}`);
    }
}

// Crear instancia única (singleton)
const datatableProfiler = new DatatablePerformanceProfiler();

// Exportar como default y named export
export default datatableProfiler;
export { DatatablePerformanceProfiler, datatableProfiler };

